/**
 * `useCrawlerAccess` — resolves `signals.crawler_access` for a site's
 * domain from `POST /v1/domain-readiness` (ADR-0207 Slice 2).
 *
 * Cached per-domain in a module-scoped `Map` so multiple surfaces that
 * mount for the same domain (setup guide + settings scan visible at
 * different times) share ONE fetch per session. The endpoint is
 * IP-rate-limited (60 / minute sliding window); a dashboard tab
 * mounting several surfaces at once should not each fire a probe.
 *
 * Fail-soft: on any error (network, non-200, malformed body) the state
 * resolves to `null` and the caller renders nothing. The WAF banner is
 * additive — it must never block the panel it sits above.
 */

import { useEffect, useState } from 'react';

import {
  checkDomainReadinessCrawlerAccess,
  type CrawlerAccess,
} from '../lib/domain-readiness.js';

type CacheEntry =
  | { status: 'in_flight'; promise: Promise<CrawlerAccess | null> }
  | { status: 'resolved'; value: CrawlerAccess | null };

const CACHE: Map<string, CacheEntry> = new Map();

/** Test-only reset — clears the module-scoped memo. */
export function __resetCrawlerAccessCacheForTests(): void {
  CACHE.clear();
}

export interface UseCrawlerAccessState {
  readonly status: 'loading' | 'ready';
  readonly access: CrawlerAccess | null;
}

/**
 * Look up the crawler_access signal for `domain`. Returns `loading`
 * until the first probe resolves; `ready` with `access` (possibly
 * `null` on error) afterward. Never throws.
 */
export function useCrawlerAccess(
  domain: string | null | undefined,
): UseCrawlerAccessState {
  const key = domain?.trim().toLowerCase() ?? '';
  const cached = key !== '' ? CACHE.get(key) : undefined;
  const initial: UseCrawlerAccessState =
    cached?.status === 'resolved'
      ? { status: 'ready', access: cached.value }
      : { status: 'loading', access: null };

  const [state, setState] = useState<UseCrawlerAccessState>(initial);

  useEffect(() => {
    if (key === '') {
      // No domain — settle to `ready + null` so downstream callers
      // never sit in perpetual `loading` for a site row without a
      // domain (defensive; sites always carry one).
      setState({ status: 'ready', access: null });
      return;
    }
    const existing = CACHE.get(key);
    if (existing?.status === 'resolved') {
      setState({ status: 'ready', access: existing.value });
      return;
    }
    let cancelled = false;
    const promise: Promise<CrawlerAccess | null> =
      existing?.status === 'in_flight'
        ? existing.promise
        : (() => {
            const p = checkDomainReadinessCrawlerAccess(key)
              .then((value) => {
                CACHE.set(key, { status: 'resolved', value });
                return value;
              })
              .catch(() => {
                CACHE.set(key, { status: 'resolved', value: null });
                return null;
              });
            CACHE.set(key, { status: 'in_flight', promise: p });
            return p;
          })();
    void promise.then((value) => {
      if (cancelled) return;
      setState({ status: 'ready', access: value });
    });
    return (): void => {
      cancelled = true;
    };
  }, [key]);

  return state;
}
